import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import KeyboardReturnIcon from '@mui/icons-material/KeyboardReturn';
import Refund from '../refund/Refund';

import {
  toTitleCase,
  convertCentsToMoney,
  getLocaleDateTimeString,
  findAccess,
  history,
} from '../../utils';

import {
  CHARGE_TRANSACTION_ID,
  HOLD_TRANSACTION_ID,
  REFUND_TRANSACTION_ID,
} from '../../constants';

import { useChargeByIdRelayQuery } from '../../hooks/transaction/query/V2/useChargesRelay';
import { useWalletTransactionByIdQuery } from '../../hooks/transaction/query/V2/useWalletTransactionRelay';
import { useRefundByIdQuery } from '../../hooks/transaction/query/V2/useRefundRelay';
import { useAdminStore } from '../../store/adminStore';

import PageContainer from '../../components/Redesing/page-container';
import PageContent from '../../components/Redesing/page-content';
import CardView from '../../components/Redesing/card-view';
import { Stack } from '@mui/system';
import { PropertyList } from '../../components/Redesing/property-list';
import { PropertyListItem } from '../../components/Redesing/property-list-item';
import PropertyListItemLink from '../../components/Redesing/property-list-item-link';
import Divider from '@mui/material/Divider';
import IconButtonMenu from '../../components/Redesing/icon-button-menu';
import { SeverityPill } from '../../components/Redesing/severity-pill';
import DetailPageSkeleton from '../../components/Redesing/skeleton/DetailPageSkeleton';

export const TransactionDetailModal = () => {
  const [showRefundModal, setRefundModal] = useState(false);
  const { transactionId } = useParams<{ transactionId: string }>();

  const systemAccess = useSelector((state: any) => state.admin.systemAccess);
  const { admin } = useAdminStore();
  const movaticAccess = admin.admin?.movaticAccess;

  const transactionIdPrefix = transactionId.slice(0, 2);
  const isCharge = transactionIdPrefix === CHARGE_TRANSACTION_ID;
  const isHold = transactionIdPrefix === HOLD_TRANSACTION_ID;
  const isRefundTransaction = transactionIdPrefix === REFUND_TRANSACTION_ID;
  const isWalletTransaction = !isCharge && !isHold && !isRefundTransaction;

  const { data: charge, isFetched: chargeFetched } = useChargeByIdRelayQuery(
    transactionId,
    { enabled: isCharge || isHold }
  );

  const { data: walletTx, isFetched: wtFetched } = useWalletTransactionByIdQuery(
    transactionId,
    {
      enabled: isWalletTransaction,
    }
  );

  const { data: refundData, isFetched: refundFetched } = useRefundByIdQuery(
    transactionId,
    { enabled: isRefundTransaction }
  );

  const loaded =
    isCharge || isHold ? chargeFetched : isRefundTransaction ? refundFetched : wtFetched;

  const transactionType = isRefundTransaction
    ? 'Refund'
    : walletTx?.type || (isHold ? 'Hold' : 'Charge');

  const refunded = charge?.refunded || walletTx?.refunded;
  const transactionStatus = charge?.refundStatus || charge?.status;

  const showRefundButton =
    !refunded &&
    (transactionType === 'Charge' || transactionType === 'Wallet Debit') &&
    (transactionStatus === 'succeeded' || transactionStatus === 'partially_refunded');

  const rentalId = charge?.rental?.id || walletTx?.rental?.id;
  const userName = charge?.user
    ? `${charge.user.firstName || ''} ${charge.user.lastName || ''}`.trim()
    : walletTx?.user
    ? `${walletTx.user.firstName || ''} ${walletTx.user.lastName || ''}`.trim()
    : '';

  const transaction = {
    type: transactionType,
    rentalId,
    taxRate: charge ? Number(charge.taxRate || 0) : Number(walletTx?.taxRate || 0),
    taxAmount: charge ? Number(charge.taxAmount || 0) : Number(walletTx?.taxAmount || 0),
    chargeTotal: charge
      ? Number(charge.chargeTotal || 0)
      : Number(walletTx?.chargeTotal || 0),
    transactionId: charge?.id || walletTx?.id || transactionId,
    created: charge?.created || walletTx?.createdOn,
    description: charge?.description || walletTx?.description,
    holdAmount: charge?.holdAmount ?? null,
    status: charge?.refundStatus || charge?.status,
  };

  const generalInfo = (info: typeof transaction) => {
    return (
      <PropertyList dataId={'general-info-transaction'}>
        <PropertyListItem
          divider
          label={'Type'}
          value={info.type}
          dataId="transaction-type"
        />
        {info.rentalId && (
          <PropertyListItemLink
            divider
            label={'Rental ID'}
            value={info.rentalId}
            dataId="transaction-rental-id"
            to={`/rentals/${info.rentalId}`}
            text={info.rentalId}
          />
        )}
        <PropertyListItem
          divider
          label="Tax Rate"
          value={`${info.taxRate || 0}%`}
          dataId="transaction-tax-rate"
        />
        <PropertyListItem
          divider
          label="Tax Collected"
          value={info.taxAmount ? convertCentsToMoney(info.taxAmount) : '$0.00'}
          dataId="transaction-tax-collected"
        />
        <PropertyListItem
          divider
          label="Subtotal"
          value={convertCentsToMoney(info.chargeTotal)}
          dataId="transaction-subtotal"
        />
        <PropertyListItem
          divider
          label="Total"
          value={convertCentsToMoney(info.chargeTotal)}
          dataId="transaction-total"
        />
        {info.holdAmount != null && info.holdAmount > 0 && (
          <PropertyListItem
            divider
            label="Hold Amount"
            value={convertCentsToMoney(info.holdAmount)}
            dataId="transaction-hold-amount"
          />
        )}
        <PropertyListItem
          divider
          label="Id"
          value={info.transactionId}
          dataId="transaction-id"
        />
        <PropertyListItem
          divider
          label="Date"
          value={getLocaleDateTimeString(info.created)}
          dataId="transaction-date"
        />
        <PropertyListItem
          divider
          label="Description"
          value={info.description}
          dataId="transaction-description"
        />
        <PropertyListItem label="Status" dataId="transaction-status">
          <SeverityPill
            color={
              info.status === 'failed'
                ? 'error'
                : info.status === 'requires_capture'
                  ? 'warning'
                  : 'success'
            }
          >
            {toTitleCase(info.status?.replace(/_/g, ' '))}
          </SeverityPill>
        </PropertyListItem>
      </PropertyList>
    );
  };

  const generalRefundInfo = (info: {
    type?: string;
    chargeTotal?: number | null;
    transactionId?: string;
    created?: string | null;
    description?: string;
    status?: string | null;
  }) => {
    return (
      <PropertyList>
        <PropertyListItem
          divider
          label="Type"
          value={info.type}
          dataId="refund-info-type"
        />
        <PropertyListItem
          divider
          label="Total"
          value={convertCentsToMoney(info.chargeTotal)}
          dataId="refund-info-total"
        />
        <PropertyListItem
          divider
          label="Id"
          value={info.transactionId}
          dataId="refund-info-id"
        />
        <PropertyListItem
          divider
          label="Date"
          value={getLocaleDateTimeString(info.created)}
          dataId="refund-info-date"
        />
        <PropertyListItem
          divider
          label="Description"
          value={info.description}
          dataId="refund-info-description"
        />
        <PropertyListItem
          divider
          label="Status"
          value={toTitleCase(info.status?.replace(/_/g, ' '))}
          dataId="refund-info-status"
        />
      </PropertyList>
    );
  };

  const button = () => {
    return (
      <IconButtonMenu
        buttons={[
          {
            label: 'Issue Refund',
            onClick: () => setRefundModal(true),
            disabled: !findAccess(systemAccess).includes('refunds') && !movaticAccess,
            dataId: 'button-refund',
            startIcon: <KeyboardReturnIcon />,
          },
        ]}
      />
    );
  };

  const refundInfo = (info: {
    refundId?: string;
    refundAmmount?: number | null;
    refundDescription?: string | null;
    refundDate?: string | null;
    refundStatus?: string;
  }) => {
    return (
      <PropertyList>
        {info.refundId && (
          <PropertyListItem
            divider
            label="Refund ID"
            value={info.refundId}
            dataId="transaction-refund-id"
          />
        )}
        <PropertyListItem
          label="Refund amount"
          value={convertCentsToMoney(info.refundAmmount)}
          dataId="transaction-refund-amount"
        />
        {info.refundDescription && (
          <>
            <Divider />
            <PropertyListItem
              label="Refund description"
              value={info.refundDescription}
              dataId="transaction-refund-description"
            />
          </>
        )}
        {info.refundDate && (
          <>
            <Divider />
            <PropertyListItem
              divider
              label="Refund Date"
              value={getLocaleDateTimeString(info.refundDate)}
              dataId="transaction-refund-date"
            />
          </>
        )}
        {info.refundStatus && (
          <>
            <Divider />
            <PropertyListItem label="Refund Status" dataId="transaction-refund-status">
              <SeverityPill
                color={
                  info.refundStatus === 'failed'
                    ? 'error'
                    : info.refundStatus === 'requires_capture'
                      ? 'warning'
                      : 'success'
                }
              >
                {toTitleCase(info.refundStatus?.replace(/_/g, ' '))}
              </SeverityPill>
            </PropertyListItem>
          </>
        )}
      </PropertyList>
    );
  };

  const refundFromTransaction = charge?.refunds?.[0]
    ? {
        refundId: charge.refunds[0].id,
        refundDate: charge.refunds[0].createdOn,
        refundAmmount: charge.refunds[0].amount,
        refundDescription: charge.refunds[0].description || undefined,
        refundStatus: charge.refunds[0].status,
      }
    : walletTx?.refunded
    ? {
        refundId: undefined as string | undefined,
        refundDate: undefined as string | undefined,
        refundAmmount: walletTx.amountRefunded,
        refundDescription: walletTx.refundDescription || undefined,
        refundStatus: undefined as string | undefined,
      }
    : null;

  const refundGral = refundData
    ? {
        type: 'Refund',
        chargeTotal: refundData.amount,
        transactionId: refundData.id,
        created: refundData.createdOn,
        description: refundData.description || undefined,
        status: refundData.status,
      }
    : null;

  const content = () => {
    const isTransactionLoaded = chargeFetched || wtFetched;

    return (
      <Stack spacing={2} data-id="charge-details-content">
        {isTransactionLoaded && !isRefundTransaction && (
          <CardView
            dataId={'charge-details'}
            content={<Stack>{generalInfo(transaction)}</Stack>}
            title={'General Information'}
          />
        )}
        {refunded && refundFromTransaction && (
          <CardView
            dataId={'refund-details'}
            content={<Stack>{refundInfo(refundFromTransaction)}</Stack>}
            title={'Refund Information'}
          />
        )}
        {refundFetched && refundGral && (
          <CardView
            dataId={'refund-details'}
            content={<Stack>{generalRefundInfo(refundGral)}</Stack>}
            title={'Refund Information'}
          />
        )}
        {showRefundModal && (
          <Refund
            show={showRefundModal}
            close={(redirect) => {
              setRefundModal(false);
              if (redirect) {
                history.push('/billing/transactions');
              }
            }}
            chargeId={transactionId}
            rentalId={rentalId}
            transaction={transaction as any}
            refundType={rentalId ? 'rental' : 'other'}
            userName={userName || ''}
            rentalToRefund={{
              billing: { total: transaction.chargeTotal },
            }}
          />
        )}
      </Stack>
    );
  };

  return (
    <PageContainer>
      {!loaded ? (
        <DetailPageSkeleton cards={[{ rows: 10, title: true }]} />
      ) : (
        <PageContent
          backLink={'/billing/transactions'}
          contentTitle={'Transactions'}
          itemName={transactionType}
          itemChipValue={transactionId}
          chipLabel={`${transactionType} ID`}
          tabsChildren={content()}
          itemTopButtons={showRefundButton ? button() : null}
        />
      )}
    </PageContainer>
  );
};

export default TransactionDetailModal;
